import { TenantDto, UserDto } from '@admin/app/api'
import { useAdminStore } from '@admin/AppProvider'
import { UserStore } from '@admin/stores/user.store'
import { useTranslate } from '@tolgee/react'
import {
  ButtonOption,
  ConfirmationDialog,
  OptionsButton,
  TomTable,
  tomTableColumnFilterPresets,
  useDefaultMRTOptions,
  useTomTable,
} from '@tom-ui/ui'
import { appStore, useTablePreferencesConfig } from '@tom-ui/utils'
import { MRT_ColumnDef } from 'material-react-table'
import Permission from 'modules/utils/src/utils/permission.enum'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useUserMessageHandler } from '../../hooks/useUserMessageHandler'

interface UsersTableProps {
  store: UserStore
  tenants: TenantDto[]
}

export const UsersTable = ({ store, tenants }: UsersTableProps) => {
  const { t } = useTranslate()
  const navigate = useNavigate()
  const defaultOptions = useDefaultMRTOptions<UserDto>()
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const { getExceptionMessage, getDeleteSuccessMessage } = useUserMessageHandler()

  const { authStore } = useAdminStore()

  const canManageTenant = authStore.isSuperUser
  const canEdit = authStore.hasPermission(Permission.WriteUsers)

  const handleDelete = async () => {
    if (!deleteId) return
    try {
      await store.delete(deleteId)

      appStore.setShowAlert('success', getDeleteSuccessMessage())
      setDeleteId(null)
    } catch (error: any) {
      appStore.setShowAlert('error', getExceptionMessage(error))
    }
  }

  const columns = useMemo<MRT_ColumnDef<UserDto>[]>(() => {
    const cols: MRT_ColumnDef<UserDto>[] = []
    if (canManageTenant)
      cols.push({
        accessorKey: 'tenantId',
        header: t('tenant', 'Tenant'),
        accessorFn: row => tenants.find(x => x.id === row.tenantId)?.name || row.tenantId,
        ...tomTableColumnFilterPresets.searchMandatory(),
      })

    cols.push(
      {
        accessorKey: 'fullName',
        header: t('name', 'Name'),
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        accessorKey: 'email',
        header: t('email', 'Email'),
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
    )

    return cols
  }, [t, canManageTenant, tenants])

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig('admin/users')

  const table = useTomTable(
    {
      columns,
      data: store.usersSorted,
      layoutMode: 'grid',
      enableRowSelection: false,
      enableTopToolbar: false,
      enableExpanding: false,
      initialState: {
        ...defaultOptions.initialState,
        columnPinning: {
          left: [...(defaultOptions.initialState?.columnPinning?.left ?? []), 'fullName'],
        },
      },
      renderRowActions: ({ row }) => {
        const options: ButtonOption[] = []

        if (canEdit) {
          options.push(
            {
              label: t('edit', 'Edit'),
              onClick: () => navigate(`${row.original.user_id}`),
            },
            {
              label: t('remove', 'Remove'),
              onClick: () => setDeleteId(row.original.user_id),
            },
          )
        }

        if (!options.length) return null

        return (
          <OptionsButton
            tooltip={t('moreOptions', 'More options')}
            options={options}
            position='right'
            testId='user-options-btn'
          />
        )
      },
    },
    undefined,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  return (
    <>
      <TomTable table={table} />
      <ConfirmationDialog
        open={deleteId !== null}
        title={t('deleteUser', 'Delete user?')}
        message={t('deleteUserMessage', 'User will be deleted.')}
        primaryActionText={t('yesDelete', 'Yes, delete')}
        closeLabel={t('noCancel', 'No, cancel')}
        maxWidth='xs'
        onConfirm={() => handleDelete()}
        onClose={() => setDeleteId(null)}
      />
    </>
  )
}
